import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Box,
    Stack,
    Button,
    Typography,
    Paper,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    List,
    ListItemText
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import DownloadForOfflineRoundedIcon from '@mui/icons-material/DownloadForOfflineRounded';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import Draggable from 'react-draggable';

import useUploadWorkflow from '../hooks/useUploadWorkflow';
import FileListItem from './FileListItem';
import DataPreviewGrid from './DataPreviewGrid';
import RunAndDownloadButtons from './common/RunAndDownloadButtons';
import UploadDropzone from './upload/UploadDropzone';
import UploadOptionFields from './upload/UploadOptionFields';
import { Section, SectionTitle, SectionBody } from './common/BasicComponents';
import { colors } from '../../../styles/colorPalette';
import * as geoConfig from '../../../config/geocoder/config';

function DraggablePaper(props) {
    const paperRef = React.useRef(null);
    return (
        <Draggable nodeRef={paperRef} handle=".geocoder-upload-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} ref={paperRef} />
        </Draggable>
    );
}

export default function FileUploadPanel({
    encodingOptions,
    delimiterOptions,
    geocodingTypeOptions,
    coordSystemOptions,
    onLayerAdd = null,
    showReverse,
    setShowReverse,
    isFileDeletedAfterUpload,
    onClearFileLayers,
    downloadFormatOptions,
}) {
    const {
        tab,
        setTab,
        resultName,
        lastStatus,
        validationMessage,
        encoding,
        onEncodingChange,
        delimiter,
        onDelimiterChange,
        geocodingType,
        onGeocodingTypeChange,
        coordSystem,
        onCoordSystemChange,
        downloadFormat,
        onDownloadFormatChange,
        downloadCoordSystem,
        onDownloadCoordSystemChange,
        unifiedAddrOptions,
        unifiedAddrField,
        onAddrFieldChange,
        xyFieldOptions,
        xField,
        yField,
        onXFieldChange,
        onYFieldChange,
        canRunForward,
        canRunReverse,
        canDownload,
        handleUploadForward,
        handleUploadReverse,
        handleDownloadOriginal,
        handleDownloadCSV,
        handleDownloadXLSX,
        handleDownloadGeoJSON,
        previewGeoJSON,
        gridRows,
        gridColumns,
        gridInfo,
        dataEpsg,
        swapXY,
        getRootProps,
        getInputProps,
        isDragActive,
        progress,
        status,
        cancel,
        showDelimiterOption,
        openFileDialog,
        file,
        resultGeoJSON,
        clearUploadedData,
    } = useUploadWorkflow({
        encodingOptions,
        delimiterOptions,
        geocodingTypeOptions,
        coordSystemOptions,
        downloadFormatOptions,
    });

    // 파일 업로드/설정 모달
    const [isModalOpen, setIsModalOpen] = useState(false);

    // 삭제 확인 모달
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    // 등록한 파일 삭제
    const handleConfirmDelete = useCallback(() => {
        clearUploadedData();
        onClearFileLayers?.();
        setIsDeleteModalOpen(false);
    }, [clearUploadedData, onClearFileLayers]);

    // 최초 로드 시 레이어 초기화
    useEffect(() => {
        console.log("파일 패널, 최초 로드 시 레이어 초기화 실행")
        clearUploadedData?.();
        onClearFileLayers?.();
    }, []);
        
    // 데이터 설정 모달에서 삭제한 경우 파일 업로드 모달 표시
    useEffect(() => {
        if (!isDeleteModalOpen && isModalOpen) {
            handleOpenUploadModal();
        }
    }, [isDeleteModalOpen]);
        
    // 모달 종류(데이터 등록 모달, 지오코딩 설정 수정 모달)
    const [isUploadModal, setIsUploadModal] = useState(true);

    // 데이터 등록 모달 열기
    const handleOpenUploadModal = () => {
        setIsUploadModal(true);
        setIsModalOpen(true);
    };

    // 설정 모달 열기
    const handleOpenSettingModal = useCallback(() => {
        setIsUploadModal(false);
        setIsModalOpen(true);
    });
    
    const lastAddedPreviewRef = useRef(null);
    
    // 데이터 업로드 시 모달 닫기
    useEffect(() => {
        if (gridRows?.length > 0 && isModalOpen) {
            if (isUploadModal) setIsModalOpen(false);
        }
    }, [gridRows?.length, isModalOpen, isUploadModal]);

    const formatFileSize = (bytes) => {
        if (!Number.isFinite(bytes)) return '-';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
    };

    const getUploadFormatLabel = () => {
        if (!file?.name) return '-';
        const lower = file.name.toLowerCase();
        if (lower.endsWith('.zip')) return 'Shapefile (ZIP)';
        const ext = lower.includes('.') ? lower.split('.').pop() : '';
        return ext ? `${ext.toUpperCase()} 파일` : '알 수 없음';
    };

    const hasUploadSummary = Boolean(file);

    const uploadMeta = useMemo(() => {
        if (!hasUploadSummary) return null;
        return {
            fileName: file?.name || resultName,
            rowCount: gridInfo?.rows ?? 0,
            coordSystem: {
                code: coordSystem?.name || '',
                label: coordSystem?.value || '',
            },
            geocodingType: geocodingType?.value || geocodingType?.name,
            encoding: encoding?.value,
            downloadFormat: downloadFormat?.value,
            delimiter: showDelimiterOption ? delimiter?.value : null,
            source: 'file',
        };
    }, [
        hasUploadSummary,
        file?.name,
        resultName,
        gridInfo?.rows,
        coordSystem?.value,
        coordSystem?.name,
        geocodingType?.value,
        geocodingType?.name,
        encoding?.value,
        downloadFormat?.value,
        showDelimiterOption,
        delimiter?.value,
    ]);

    const actionButtonSx = {
        minWidth: 0,
        fontWeight: 700,
        py: 1.25,
    };

    const runForwardButton = (
        <Button
            variant="contained"
            startIcon={<PlayArrowRoundedIcon />}
            onClick={handleUploadForward}
            disabled={!canRunForward}
            sx={actionButtonSx}
            fullWidth
        >
            지오코딩 실행
        </Button>
    );

    const runReverseButton = (
        <Button
            variant="contained"
            startIcon={<PlayArrowRoundedIcon />}
            onClick={handleUploadReverse}
            disabled={!canRunReverse}
            sx={actionButtonSx}
            fullWidth
        >
            리버스 지오코딩 실행
        </Button>
    );

    const downloadMenuOptions = [
        {
            value: 'original',
            label: '결과 다운로드(원본)',
            icon: <FileDownloadRoundedIcon fontSize="small" />,
            action: handleDownloadOriginal,
            disabled: !canDownload,
        },
        {
            value: 'csv',
            label: 'CSV 저장',
            icon: <DownloadForOfflineRoundedIcon fontSize="small" />,
            action: handleDownloadCSV,
            disabled: !canDownload,
        },
        {
            value: 'xlsx',
            label: 'XLSX 저장',
            icon: <DownloadForOfflineRoundedIcon fontSize="small" />,
            action: handleDownloadXLSX,
            disabled: !canDownload,
        },
        {
            value: 'geojson',
            label: 'GeoJSON 저장',
            icon: <MapRoundedIcon fontSize="small" />,
            action: handleDownloadGeoJSON,
            disabled: !previewGeoJSON,
        },
    ];
    const [downloadSelection, setDownloadSelection] = useState('');
    const handleDownloadSelect = (value) => {
        const option = downloadMenuOptions.find((opt) => opt.value === value);
        if (option && !option.disabled) {
            option.action();
        }
        setDownloadSelection('');
    };

    const executionSummary = hasUploadSummary
        ? [
              { label: '파일명', value: file?.name || '-' },
              { label: '파일 크기', value: formatFileSize(file?.size) },
              { label: '행', value: gridInfo?.rows ?? '-' },
              { label: '업로드 형식', value: getUploadFormatLabel() },
              { label: '지오코딩 유형', value: geocodingType?.value || '-' },
              { label: '좌표계', value: coordSystem?.value || '-' },
              { label: '인코딩', value: encoding?.value || '-' },
              ...(showDelimiterOption ? [{ label: '구분자', value: delimiter?.value || '-' }] : []),
              { label: '다운로드 형식', value: downloadFormat?.value || '-' },
              { label: '다운로드 좌표계', value: downloadCoordSystem?.value || '-' },
          ]
        : [];

    useEffect(() => {
        if (!onLayerAdd || !resultGeoJSON) return;
        if (lastAddedPreviewRef.current === resultGeoJSON) return;

        const fallbackMeta = {
            fileName: resultName,
            rowCount: resultGeoJSON?.features?.length ?? 0,
            coordSystem: coordSystem?.value || coordSystem?.name || '',
            geocodingType: geocodingType?.value || geocodingType?.name || '',
            encoding: encoding?.value,
            downloadFormat: downloadFormat?.value,
            delimiter: showDelimiterOption ? delimiter?.value : null,
            source: 'file',
        };

        const metaWithTimestamp = {
            ...(uploadMeta || fallbackMeta),
            timestamp: new Date().toISOString(),
        };

        onLayerAdd(
            resultGeoJSON,
            metaWithTimestamp.fileName || resultName,
            metaWithTimestamp
        );
        lastAddedPreviewRef.current = resultGeoJSON;
    }, [
        resultGeoJSON,
        uploadMeta,
        onLayerAdd,
        resultName,
        coordSystem?.value,
        coordSystem?.name,
        geocodingType?.value,
        geocodingType?.name,
        encoding?.value,
        downloadFormat?.value,
        showDelimiterOption,
        delimiter?.value,
    ]);

    // MenuItem 클릭 핸들러
    const handleDownloadItemClick = (fileFormat) => {
        const mode = showReverse ? 'reverse' : 'forward'; 
        handleDownloadSelect(fileFormat, mode); 
    };

    const handleResultMsg = () => {
        if (progress === 100) {
            const regex = /총\s*:\s*([\d,]+)건,\s*성공\s*:\s*([\d,]+)건,\s*실패\s*:\s*([\d,]+)건\s*완료되었습니다/;
            const match = status.replace(/\./g, '').match(regex);

            if (match) {
                // match[1]은 총 건수, match[2]는 성공 건수, match[3]은 실패 건수
                const total = match[1];
                const success = match[2];
                const failure = match[3];
                
                return `${total}건의 데이터 처리 완료(성공 ${success}건 / 실패 ${failure}건)`;
            }
        };
        return status;
    }

    return (
        <Box>
            <Stack 
                spacing={2}
                sx={{
                    mb: 5,
                }}
            >
            {gridRows?.length === 0 ? (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            startIcon={<AddRoundedIcon fontSize="large" />}
                            onClick={handleOpenUploadModal}
                            size="small"
                            sx={{
                                height: geoConfig.CONTROL_HEIGHT,
                                fontWeight: 500,
                                fontSize: '14px',
                                bgcolor: geoConfig.themeColor,
                                // borderRadius: '4px',
                                mt: 2,
                                px: 3,
                                // py: 0.75,
                                textTransform: 'none',
                                boxShadow: 'none',
                                '& .MuiButton-startIcon': {
                                    mr: 0.75,
                                    mb: 0.25,
                                    // '& svg': { width: 14, height: 14 },
                                },
                                '&:hover': { 
                                    bgcolor: colors.primaryBlue,
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            데이터 등록
                        </Button>
                    </Box>

                    <Box>
                        <Typography
                            variant="body2"
                            sx={{
                                textAlign: 'center', 
                                color: '#666',
                                fontSize: '0.8rem',
                                mt: 3,
                                mb: 5,
                            }}
                        >
                            데이터 등록 버튼을 클릭하여<br/>지오코딩을 실행할 파일을 업로드하세요.
                        </Typography>
                    </Box>
                </>
            ) : (
                <>
                    {/* 파일 목록 */}
                    <FileListItem
                        executionSummary={executionSummary}
                        onSettingClick={handleOpenSettingModal}
                        clearUploadedData={clearUploadedData}
                        showReverse={showReverse}
                        setShowReverse={setShowReverse}
                        encodingOptions={encodingOptions}
                        encoding={encoding}
                        onEncodingChange={onEncodingChange}
                        delimiterOptions={delimiterOptions}
                        delimiter={delimiter}
                        onDelimiterChange={onDelimiterChange}
                        showDelimiterOption={showDelimiterOption}
                        unifiedAddrOptions={unifiedAddrOptions}
                        unifiedAddrField={unifiedAddrField}
                        onAddrFieldChange={onAddrFieldChange}
                        xyFieldOptions={xyFieldOptions}
                        xField={xField}
                        onXFieldChange={onXFieldChange}
                        yField={yField}
                        onYFieldChange={onYFieldChange}
                        geocodingTypeOptions={geocodingTypeOptions}
                        geocodingType={geocodingType}
                        onGeocodingTypeChange={onGeocodingTypeChange}
                        coordSystemOptions={coordSystemOptions}
                        coordSystem={coordSystem}
                        onCoordSystemChange={onCoordSystemChange}
                        downloadFormatOptions={downloadFormatOptions}
                        downloadFormat={downloadFormat}
                        onDownloadFormatChange={onDownloadFormatChange}
                        downloadCoordSystem={downloadCoordSystem}
                        onDownloadCoordSystemChange={onDownloadCoordSystemChange}
                        isFileDeletedAfterUpload={isFileDeletedAfterUpload}
                        isDeleteModalOpen={isDeleteModalOpen}
                        setIsDeleteModalOpen={setIsDeleteModalOpen}
                        handleConfirmDelete={handleConfirmDelete}
                        progress={progress}
                    />
                    <Stack spacing={1.25}>
                        <RunAndDownloadButtons 
                            showReverse={showReverse}
                            handleQuery={showReverse ? handleUploadReverse : handleUploadForward}
                            downloadOptions={downloadMenuOptions}
                            // address={address}
                            handleDownloadItemClick={handleDownloadItemClick}
                            startIconName="PlayArrowRoundedIcon"
                            panelMode="2"
                            canDownload={canDownload}
                        />
                    </Stack>
                </>
            )}
            </Stack>

            {progress !== -1 && (
                <Paper variant="outlined" sx={{ p: 1.5, border: '1px solid #EBF6FF', borderRadius: 0, bgcolor: '#EBF6FF', mb: 6, }}>
                    <Stack
                        direction="row" 
                        alignItems="center" 
                        spacing={0.5} 
                        sx={{ mb: 1.5 }}
                    >
                        <InfoIcon 
                            fontSize="small" 
                            sx={{ color: '#007ed0' }} 
                        />
                        <Typography 
                            variant="subtitle1" 
                            fontWeight={700}
                        >
                            {progress >= 0 && progress < 100 ? `${Math.round(progress)}% 진행 중` : '지오코딩 완료'}
                        </Typography>
                    </Stack>
                    <Stack spacing={0.5} sx={{ ml: 0.5 }}>
                        <Box
                            sx={{
                                alignItems: 'center',
                                gap: 2,
                            }}
                        >
                            {progress >= 0 && progress < 100 ? <LinearProgress variant="determinate" value={Math.max(0, progress)} /> : null}
                            <Typography sx={{ mt: 1, fontWeight: 500 }}>
                                {/* {progress === 100 ? status.replaceAll(':', '').split('.')[0] : status} */}
                                {handleResultMsg()}
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>
            )}

            {/* <ProgressModal open={progress > -1} progress={progress} status={status} onClose={cancel} /> */}

            <Dialog
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                maxWidth={isUploadModal ? "sm" : "xl"}
                fullWidth
                PaperComponent={DraggablePaper}
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        overflow: 'hidden',
                        width: 'min(1080px, calc(100vw - 48px))',
                    },
                }}
            >
                <DialogTitle
                    className="geocoder-upload-dialog-title"
                    sx={{ cursor: 'move', fontWeight: 700 }}
                >
                    {isUploadModal ? '주소 찾기 파일 업로드' : '주소 찾기 데이터 설정'}

                    {/* 닫기 버튼 */}
                    <IconButton
                        aria-label="close"
                        onClick={() => setIsModalOpen(false)}
                        sx={{
                            position: 'absolute',
                            right: 12, 
                            top: 8, 
                            color: (theme) => theme.palette.grey[600],
                            '&:hover': {
                                bgcolor: 'transparent', 
                                color: '#595959',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {isUploadModal ? (
                        <Stack>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                }}
                            >
                                데이터 업로드
                            </Typography>
                            <List
                                sx={{
                                    pt: 0,
                                    pb: 1,
                                    '& .MuiListItemText-primary': {
                                        fontSize: 12.5, 
                                        color: colors.lightGray,
                                        ml: 1,
                                    },
                                }}
                            >
                                <ListItemText>
                                    • ZIP(Shape), CSV, XLS, XLSX 형태의 파일을 등록할 수 있습니다.
                                </ListItemText>
                                <ListItemText>
                                    • 파일의 크기는 10MB를 초과할 수 없으며, 1개의 파일만 등록할 수 있습니다.
                                </ListItemText>
                            </List>
                            <UploadDropzone
                                getRootProps={getRootProps}
                                getInputProps={getInputProps}
                                isDragActive={isDragActive}
                                onBrowseClick={openFileDialog}
                            />
                        </Stack>
                    ) : (
                        <Stack spacing={2}>
                            <Section>
                                <SectionTitle>업로드된 파일</SectionTitle>
                                <SectionBody>
                                    <Box
                                        sx={{
                                            border: '1px solid #b9b9b9',
                                            px: 1.5,
                                            py: 1,
                                            display: 'flex',
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}>
                                            <InsertDriveFileOutlinedIcon sx={{ color: '#686868'}} />
                                            <Typography sx={{ fontSize: 14 }}>{file?.name}</Typography>
                                        </Box>
                                        <Box 
                                            onClick={() => setIsDeleteModalOpen(true)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                cursor: 'pointer'
                                            }}>
                                            <Typography sx={{ fontSize: 14 }}>삭제</Typography>
                                            <CancelIcon fontSize="small" sx={{ color: '#686868' }}/>
                                        </Box>
                                    </Box>
                                </SectionBody>
                            </Section>
                            <UploadOptionFields
                                showReverse={showReverse}
                                setShowReverse={setShowReverse}
                                tab={tab}
                                setTab={setTab}
                                encodingOptions={encodingOptions}
                                encoding={encoding}
                                onEncodingChange={onEncodingChange}
                                delimiterOptions={delimiterOptions}
                                delimiter={delimiter}
                                onDelimiterChange={onDelimiterChange}
                                showDelimiterOption={showDelimiterOption}
                                unifiedAddrOptions={unifiedAddrOptions}
                                unifiedAddrField={unifiedAddrField}
                                onAddrFieldChange={onAddrFieldChange}
                                xyFieldOptions={xyFieldOptions}
                                xField={xField}
                                onXFieldChange={onXFieldChange}
                                yField={yField}
                                onYFieldChange={onYFieldChange}
                                geocodingTypeOptions={geocodingTypeOptions}
                                geocodingType={geocodingType}
                                onGeocodingTypeChange={onGeocodingTypeChange}
                                coordSystemOptions={coordSystemOptions}
                                coordSystem={coordSystem}
                                onCoordSystemChange={onCoordSystemChange}
                            />
                            <Section>
                                <Box
                                    sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center', 
                                        mb: 0.5,
                                    }}
                                >
                                    <SectionTitle>데이터 미리보기</SectionTitle>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {`${gridInfo.rows}행 / ${gridInfo.columns}열 (${dataEpsg || '알수없음'})`}
                                    </Typography>
                                </Box>
                                <SectionBody>
                                    <Box>
                                        <DataPreviewGrid rows={gridRows} columns={gridColumns} height={260} perPage={5} />
                                    </Box>
                                </SectionBody>
                            </Section>
                            
                            <DialogActions sx={{ justifyContent: 'flex-end', mt: '0 !important', p: 0 }}>
                                <Button
                                    variant="contained"
                                    onClick={() => setIsModalOpen(false)}
                                    sx={{
                                        bgcolor: colors.darkBlue,
                                        px: 4,
                                    }}
                                >
                                    적용
                                </Button>
                            </DialogActions>
                        </Stack>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}